import { useState, useCallback } from 'react';
import { createCheckin, getCheckinsByClient } from '../api/client';
import type { Checkin, CreateCheckinInput } from '../types';

interface UseCheckinsReturn {
  checkins: Checkin[];
  isLoading: boolean;
  error: string | null;
  submitCheckin: (input: CreateCheckinInput) => Promise<Checkin>;
  fetchByClient: (clientId: string) => Promise<void>;
  clearError: () => void;
}

export function useCheckins(): UseCheckinsReturn {
  const [checkins, setCheckins] = useState<Checkin[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitCheckin = useCallback(async (input: CreateCheckinInput): Promise<Checkin> => {
    setIsLoading(true);
    setError(null);
    try {
      const checkin = await createCheckin(input);
      setCheckins(prev => [checkin, ...prev]);
      return checkin;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit checkin';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchByClient = useCallback(async (clientId: string): Promise<void> => {
    const token = localStorage.getItem('ptai_token');
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getCheckinsByClient(clientId);
      setCheckins(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch checkins';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    checkins,
    isLoading,
    error,
    submitCheckin,
    fetchByClient,
    clearError,
  };
}
